// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { WarningAmberRounded } from '@/constants/hardfin-standard-icons';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { SparseCustomer, SparseLocation } from '@/sparse/api/types';
import { Alert, AlertProps, Collapse } from '@mui/material';

/**
 * Returns a warning message for locations that do not match the customer.
 *
 * @param locations - The locations to check against the customer.
 * @param customer - The customer to which the locations should belong.
 */
function getLocationWarningLabel(
  locations: SparseLocation[],
  customer: SparseCustomer | null
) {
  if (customer === null) {
    return null;
  }

  if (locations.length === 0) {
    return null;
  }

  if (locations.length === 1) {
    return `${getEffectiveLocationName(locations[0])} is not associated with ${customer.name}`;
  }

  if (locations.length === 2) {
    return `${getEffectiveLocationName(locations[0])} and ${getEffectiveLocationName(locations[1])} are not associated with ${customer.name}`;
  }

  if (locations.length > 2) {
    const joined = locations
      .slice(0, locations.length - 1)
      .map((location) => getEffectiveLocationName(location))
      .join(', ');

    const lastLocation = locations[locations.length - 1];

    return `${joined}, and ${getEffectiveLocationName(lastLocation)} are not associated with ${customer.name}`;
  }
}

interface Props extends Pick<AlertProps, 'sx'> {
  /**
   * The customer to which the locations should belong.
   */
  customer: SparseCustomer | null;

  /**
   * The locations to check against the customer.
   */
  locations: SparseLocation[];
}

/**
 * A warning that is visible when the one or more of the locations do not match
 * the customer.
 */
export function ProjectLocationFormWarning(props: Props) {
  const locations = props.locations.filter((location) => {
    if (props.customer === null) {
      return false;
    }

    return location.customer?.id !== props.customer.id;
  });

  const warning = getLocationWarningLabel(locations, props.customer);
  const isOpen = Boolean(warning);

  return (
    <Collapse in={isOpen}>
      <Alert severity="warning" sx={props.sx} icon={<WarningAmberRounded />}>
        {warning}
      </Alert>
    </Collapse>
  );
}
